import { FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface ContactEmailFormProps {
  source?: string;
  footerRight?: React.ReactNode;
}

const MAX_NAME = 80;
const MAX_EMAIL = 120;
const MAX_SUBJECT = 140;
const MAX_MESSAGE = 2000;
const MIN_MESSAGE = 10;
const FUNCTION_TIMEOUT_MS = 15000;
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const toSpanishError = (err: unknown) => {
  const message = err instanceof Error ? err.message : String(err || "");
  const lower = message.toLowerCase();
  if (lower.includes("edge function returned a non-2xx")) {
    return "No se pudo enviar ahora mismo. Prueba de nuevo en unos segundos.";
  }
  if (lower.includes("failed to fetch") || lower.includes("network")) {
    return "No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.";
  }
  if (lower.includes("tiempo de espera")) return "La solicitud tardó demasiado. Prueba de nuevo.";
  return "No se pudo enviar el mensaje. Inténtalo de nuevo.";
};

const ContactEmailForm = ({ source = "contact-page", footerRight }: ContactEmailFormProps) => {
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [lastSentAt, setLastSentAt] = useState<number>(0);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const turnstileContainerId = "turnstile-contact-container";

  useEffect(() => {
    if (!turnstileSiteKey) return;
    const renderInvisibleWidget = () => {
      const api = (window as any).turnstile;
      const container = document.getElementById(turnstileContainerId);
      if (!api || !container || container.childElementCount > 0) return;
      api.render(`#${turnstileContainerId}`, {
        sitekey: turnstileSiteKey,
        size: "normal",
        execution: "execute",
      });
      setTurnstileReady(true);
    };

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (!existingScript) {
      const script = document.createElement("script");
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => renderInvisibleWidget();
      document.head.appendChild(script);
    } else if ((window as any).turnstile) {
      renderInvisibleWidget();
    } else {
      existingScript.addEventListener("load", renderInvisibleWidget);
    }
  }, [turnstileSiteKey, turnstileContainerId]);

  const runInvisibleTurnstile = async () => {
    if (!turnstileSiteKey) return "";
    const api = (window as any).turnstile;
    if (!api) throw new Error("No se pudo iniciar la verificación anti-bot.");
    return api.execute(`#${turnstileContainerId}`, { async: true }) as Promise<string>;
  };

  const update = (key: keyof typeof form, value: string) => setForm((c) => ({ ...c, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const now = Date.now();
    if (now - lastSentAt < 30000) {
      toast.error("Espera unos segundos antes de volver a enviar.");
      return;
    }
    if (form.website.trim()) {
      toast.error("No se pudo enviar el mensaje.");
      return;
    }
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Completa nombre, email y mensaje");
      return;
    }
    if (form.message.trim().length < MIN_MESSAGE) {
      toast.error(`El mensaje debe tener al menos ${MIN_MESSAGE} caracteres`);
      return;
    }
    if (turnstileSiteKey && !turnstileReady) {
      toast.error("La verificación anti-bot aún no está lista. Inténtalo en unos segundos.");
      return;
    }
    setSubmitting(true);
    try {
      const turnstileToken = await runInvisibleTurnstile();
      const invokePromise = supabase.functions.invoke("send-contact-email", {
        body: {
          source,
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          subject: form.subject.trim(),
          message: form.message.trim(),
          website: form.website.trim(),
          turnstileToken: turnstileToken || null,
        },
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Tiempo de espera agotado. Prueba de nuevo.")), FUNCTION_TIMEOUT_MS)
      );
      const { data, error } = await Promise.race([invokePromise, timeoutPromise]) as Awaited<typeof invokePromise>;
      if (error) throw error;
      if (data?.warning) toast.success("Mensaje guardado.");
      else toast.success("Mensaje enviado.");
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
      setLastSentAt(now);
    } catch (err) { toast.error(toSpanishError(err)); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none transition-colors focus:border-primary/50";

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <input type="text" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Tu nombre" className={inputCls} maxLength={MAX_NAME} required />
        <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="Tu email" className={inputCls} maxLength={MAX_EMAIL} required />
      </div>
      <input type="text" value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder="Asunto" className={inputCls} maxLength={MAX_SUBJECT} />
      <input
        type="text"
        value={form.website}
        onChange={(e) => update("website", e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />
      <div className="relative">
        <textarea
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Escribe tu mensaje..."
          rows={5}
          maxLength={MAX_MESSAGE}
          className={`${inputCls} leading-6 pr-16`}
          required
        />
        <span className="pointer-events-none absolute bottom-2 right-3 text-[11px] text-muted-foreground">
          {form.message.length}/{MAX_MESSAGE}
        </span>
      </div>
      {turnstileSiteKey && (
        <div id={turnstileContainerId} className="fixed -left-[9999px] top-0 opacity-0 pointer-events-none" aria-hidden="true" />
      )}
      <div className="flex items-center justify-between gap-3 pt-1">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-foreground px-4 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? "Enviando..." : "Enviar"}
        </button>
        {footerRight ? <div className="text-sm">{footerRight}</div> : null}
      </div>
    </form>
  );
};

export default ContactEmailForm;
